/**
 * Feed store. `selectedFeed`, `selectedTypes`, `groupSelectedTypes` and
 * `guestFeedLanguages` are persisted to localStorage (`oh-feed`).
 * `refreshSignal` is incremented to trigger a feed refetch without
 * unmounting the list.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { FeedType, Language, PostType } from '@olimhub/shared'

// 'action' has no backend PostType yet (Nível 2 chip is UI-only until the
// Action/EOt tool exists) — kept as a client-only union rather than adding
// a fake value to the shared PostType enum.
export type FeedTypeChip = PostType | 'action'

interface FeedState {
  selectedFeed: FeedType
  // Nav refactor (Nível 2 chips) — multi-select post-type filter, layered on
  // top of the Local/Following "lens" (selectedFeed). Empty array = no
  // filter, show everything — never an implicit default type.
  selectedTypes: FeedTypeChip[]
  // Nível 2 chip filter for group feeds — keyed by group slug so each group
  // keeps its own independent selection. A filter chosen in Home or in one
  // group must never leak into another feed surface (follow-up fix on the
  // nav refactor, 2026-07-31 — the two were briefly wired to share
  // `selectedTypes`, which was wrong).
  groupSelectedTypes: Record<string, FeedTypeChip[]>
  guestFeedLanguages: Language[]
  refreshSignal: number
  setFeed: (feed: FeedType) => void
  setSelectedTypes: (types: FeedTypeChip[]) => void
  toggleType: (type: FeedTypeChip) => void
  setGroupSelectedTypes: (slug: string, types: FeedTypeChip[]) => void
  toggleGroupType: (slug: string, type: FeedTypeChip) => void
  setGuestFeedLanguages: (langs: Language[]) => void
  triggerRefresh: () => void
}

export const useFeedStore = create<FeedState>()(
  persist(
    (set, get) => ({
      selectedFeed: 'local',
      selectedTypes: [],
      groupSelectedTypes: {},
      guestFeedLanguages: [],
      refreshSignal: 0,
      setFeed: (feed) => set({ selectedFeed: feed }),
      setSelectedTypes: (types) => set({ selectedTypes: types }),
      toggleType: (type) => {
        const current = get().selectedTypes
        set({
          selectedTypes: current.includes(type)
            ? current.filter((t) => t !== type)
            : [...current, type],
        })
      },
      setGroupSelectedTypes: (slug, types) => {
        set({ groupSelectedTypes: { ...get().groupSelectedTypes, [slug]: types } })
      },
      toggleGroupType: (slug, type) => {
        const current = get().groupSelectedTypes[slug] ?? []
        const next = current.includes(type) ? current.filter((t) => t !== type) : [...current, type]
        set({ groupSelectedTypes: { ...get().groupSelectedTypes, [slug]: next } })
      },
      setGuestFeedLanguages: (langs) => set({ guestFeedLanguages: langs }),
      triggerRefresh: () => set((s) => ({ refreshSignal: s.refreshSignal + 1 })),
    }),
    {
      name: 'oh-feed',
      partialize: (state) => ({
        selectedFeed: state.selectedFeed,
        selectedTypes: state.selectedTypes,
        groupSelectedTypes: state.groupSelectedTypes,
        guestFeedLanguages: state.guestFeedLanguages,
      }),
    },
  ),
)
